import { EventNameFormat, TrackingEvent } from 'types/tracking'
import posthog, { CaptureOptions, Properties } from 'posthog-js'
import { UrlSearchParamsKeys } from 'store/app/types.ts'
import { DEBUG_POSTHOG, IS_PRODUCTION, POSTHOG_DEV_ENABLED_TRACKING, POSTHOG_HOST, POSTHOG_KEY } from 'constants/envs.ts'
import { getSessionUserType } from 'utils/userType.ts'
import { UserTypes } from 'store/auth/types.ts'
import { TRACKING_PARAMS } from 'constants/tracking.ts'
import { store } from 'store/index.ts'
import { Capacitor } from '@capacitor/core'
import { getCspNonce } from 'utils/csp.ts'

/**
 * Init posthog
 */
export const initializePosthog = () => {
  if (!POSTHOG_KEY || !POSTHOG_HOST) {
    if (import.meta.env.DEV) {
      const missingVariable = !POSTHOG_KEY ? 'VITE_POSTHOG_KEY' : 'VITE_POSTHOG_HOST'
      throw new Error(
        `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`
      )
    }

    return
  }

  // Fetch the URL params
  const search = new URLSearchParams(window.location.search)
  const distinctId = search.get(UrlSearchParamsKeys.POSTHOG_DISTINCT_ID)
  const sessionId = search.get(UrlSearchParamsKeys.POSTHOG_SESSION_ID)

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: '2025-05-24',
    //capture_exceptions: true, // This enables capturing exceptions using Error Tracking
    person_profiles: 'always',
    debug: !IS_PRODUCTION && DEBUG_POSTHOG,
    prepare_external_dependency_script: (script) => {
      const nonce = getCspNonce()
      if (nonce) script.nonce = nonce
      return script
    },
    bootstrap: {
      sessionID: sessionId || undefined,
      distinctID: distinctId || undefined
    },
    before_send: (event) => {
      const userType = getSessionUserType()
      if (userType === UserTypes.SUPER_USER || userType === UserTypes.SALES) {
        return null
      }

      if (event) {
        event.properties['_instance'] = 'portal'
      }

      return event
    },
    loaded: (posthog) => {
      const prodOrSpecificDevOnly = IS_PRODUCTION || (!IS_PRODUCTION && POSTHOG_DEV_ENABLED_TRACKING)

      if (!prodOrSpecificDevOnly) {
        posthog.opt_out_capturing()
      }
    }
  })

  posthog.register({ app_platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web' })
}

/**
 * Tracks an event using PostHog
 * @param {TrackingEvent} event - Object of params to format the event name. (snake_case)
 * @param {Properties} data - Additional properties to send with the event.
 * @param {CaptureOptions} options - Optional capture options for PostHog.
 */
export const trackEvent = (event: TrackingEvent, data?: Properties, options?: CaptureOptions) => {
  const eventName: EventNameFormat = event.object
    ? `${event.category}:${event.object}_${event.action}`
    : `${event.category}:${event.action}`

  // Add current language to the event data to be able to filter all events by language in PostHog if necessary.
  // User behavior between can differ greatly between French & Dutch-speaking users.
  const dataWithLanguage = {
    ...data,
    [TRACKING_PARAMS.currentLanguage]: store.getState().app.language
  }

  posthog.capture(eventName, dataWithLanguage, options)
}

/**
 * Identifies an authenticated user with their stable application identifier.
 * @param {string} distinctId
 * @param {Record<string, unknown>} data
 */
export const identifyUser = (distinctId: string, data: Record<string, unknown>) => {
  if (!distinctId) return

  // Ensure email is lowercase if it exists
  if (data?.email) {
    data.email = (data.email as string).toLowerCase()
  }

  posthog.identify(distinctId, data)
}
